import * as z from "@-/com.npmjs.zod";
import { onboardingStateSchema } from "@-/dev.improve.haniverse.lib.onboarding.state";
import { REVIEW_FOCUSES } from "@-/dev.improve.haniverse.lib.learn.review-focus";
import { STUDY_ENVIRONMENTS } from "@-/dev.improve.haniverse.lib.learn.study-environment";
import {
  DEFAULT_SLIDE_TEXT_SIZE,
  SLIDE_TEXT_SIZES,
} from "@-/dev.improve.haniverse.lib.slide.text-size";

export const deviceSettingsSchemas = {
  // The email of this device's local account, and what it takes to enter the native app: the
  // `(app)` layout sends a device without one to the welcome gate (`routes.nav.app.auth`), so every
  // screen behind it has an address to show (see `useLocalAccount`).
  //
  // "Get Started" records the typed address here and nothing else: no request and no server
  // account yet, so it works offline and the app is usable immediately. That is an anonymous local
  // account whose email still needs verification: signing up with it and entering the emailed code
  // activates it, and until then the address can be changed freely (nothing was ever sent). Every
  // sign-in or sign-up records the session's address instead, so the account keeps its email when
  // the session later expires. Only sign-out clears it, which is what returns the device to the
  // welcome gate. Device-local (MMKV): it is read synchronously at startup, before any database
  // opens or any session request.
  accountEmail: z.string().nullable().default(null),
  // A new learner's onboarding (`lib/onboarding`): the globe and its questions, then the tour that
  // unlocks the strip one destination at a time. `null` for anyone who never started one (every
  // account that signed in rather than up): the app is theirs as it is. Set by leaving the welcome
  // gate with a new account, which is before any actor database opens, so it lives on the device
  // like `accountEmail`. Another device of the same account simply shows the whole app.
  onboarding: onboardingStateSchema.nullable().default(null),
  // Device-local navigation preference; available before signing in.
  isEdgeNavigationEnabled: z.boolean().default(true),
  // Web: whether a page's section sidebar (Settings' sections, Browse's tabs) is collapsed to its
  // toggle. `null` until the viewer chooses one, while narrow windows start collapsed. Device-local,
  // so every section sidebar opens the way the viewer last left one.
  isWebSectionSidebarCollapsed: z.boolean().nullable().default(null),
  // Per actor, persisted across restarts. The local-day cohort never grows with new lessons.
  dailyReviewMilestones: z
    .record(
      z.string(),
      z.object({
        cardIds: z.array(z.string()),
        completed: z.boolean(),
        day: z.string(),
        startedAt: z.string(),
      }),
    )
    .default({}),
  // The web daily-reward popover is shown once per local day and account, even if left unclaimed.
  dailyRewardPopoverSeenDays: z.record(z.string(), z.string()).default({}),
  // Whether grapheme mnemonic illustrations (the meaning hero image and the pronunciation image)
  // are shown. Experimental and off by default: many images are still unfinished. Toggled from
  // Settings → Advanced → "Mnemonic Images".
  isGraphemeImagesEnabled: z.boolean().default(false),
  // Experimental Cookbook destination (drawer + optional primary-nav tab). Off by default; toggled
  // from Settings → Admin → Admin settings so it never ships as a default surface.
  isCookbookEnabled: z.boolean().default(false),
  isLocalBackupEnabled: z.boolean().default(true),
  // Map: ignore taps on places the learner has not unlocked yet, so small unlocked regions are
  // easier to hit among locked neighbours. Device-local (MMKV); toggled from the Map toolbar menu.
  mapDisableLockedAreas: z.boolean().default(false),
  // Map: the region whose map is open (the Map toolbar's tabs), so the tab reopens on it. The
  // ids are `CHINA_MAP_REGION_IDS`, repeated here so that reading a setting does not load the maps.
  mapRegion: z.enum(["mainland", "taiwan", "hong-kong", "macau", "singapore"]).default("mainland"),
  // Recovery is local until an explicit cloud reconciliation. Survives restart.
  isBackupRecoveryHoldEnabled: z.boolean().default(false),
  isBackupRecoveryHoldMigrated: z.boolean().default(false),
  // The last local backup attempt's failure as one precise sentence (`BackupFailure.message`), or
  // null once a backup succeeds. Device-local (MMKV) so Settings → Backups still shows it after a
  // restart.
  localBackupLastFailure: z.string().nullable().default(null),
  // This install's identity for cross-device sync pushes: the server keys push registrations by it
  // and skips the sender when it nudges the account's other devices. `installMarker` is the
  // platform's install-scoped id when the id was minted (iOS vendor id, Android id). A backup
  // restored onto another phone carries this record but not the marker, so a mismatch mints a new
  // id instead of letting two phones share one (`getDeviceId`). It names the install, not the
  // account, so it survives sign-out.
  deviceId: z.object({ id: z.string(), installMarker: z.string() }).nullable().default(null),
  // The silent-push catch-up signal (`remote-change-signal.ts`). A sync push bumps
  // `remoteChangeSignalSeq` when another of the account's devices changed the database; a pull that
  // started after the bump records it in `remoteChangeCaughtUpSeq`. Unequal means this device knows
  // it is behind, the only case in which Learn's Start waits for the cloud. Read synchronously at
  // startup, before any database opens.
  remoteChangeCaughtUpSeq: z.number().int().nonnegative().default(0),
  remoteChangeSignalSeq: z.number().int().nonnegative().default(0),
  // The push registration last accepted by the server, so a launch only registers again when the
  // account, the install or a token changed, or the registration is a week old.
  syncPushRegistration: z
    .object({
      deviceId: z.string(),
      devicePushToken: z.string(),
      expoPushToken: z.string(),
      registeredAt: z.number(),
      userId: z.string(),
    })
    .nullable()
    .default(null),
  // Default visibility of accompanying slides on Browse level surfaces (card + slides = one lesson).
  showSlideLessons: z.boolean().default(true),
  // When enabled, morpheme/card tiles whose grapheme (or word) file is not yet
  // version-complete (`version >= 0`) render at opacity-25 so unfinished mnemonics stand out in
  // authoring grids. Distinct from SRS mastery and from hide-completed filtering.
  fadeMissingMnemonicEnabled: z.boolean().default(false),
  // Debug-only renderer override for glyphs in buttons throughout the app.
  svgTileGlyphsEnabled: z.boolean().default(false),
  // Whether the grapheme-file-editor's voice/text commands use CHORD mode (terse
  // spoken "voice chords" like `no sir … control enter`) instead of the default
  // natural-language command mode ("text … in note"). Device-local (MMKV) and
  // toggled from a chip in the editor's header strip — it selects which PARSER the
  // editor uses; both modes drive the same command handlers. See the editor's
  // `readme.chord.md`.
  chordModeEnabled: z.boolean().default(false),
  // Persist the guest milestone dismissal so continuing without an account stays frictionless.
  hasDismissedFirstLevelAccountPrompt: z.boolean().default(false),
  // Whether the paywall has already been auto-presented at the free-tier boundary (the learner
  // cleared every review and every lesson in the free-tier levels). The
  // paywall opens BY ITSELF exactly once; after that the boundary is a screen they can act on at
  // their own pace (see `LearnSummaryFreeTierCompleteView`) rather than a modal that reappears
  // every time they finish their reviews. Device-local (MMKV) like the other one-shot prompts:
  // it is "has this device already interrupted them once", and it must work signed-out, since
  // buying premium never required a Haniverse account.
  hasAutoPresentedFreeTierPaywall: z.boolean().default(false),
  hideCompletedAuthoringCardsEnabled: z.boolean().default(false),
  // The newest changelog entry date (`YYYY-MM-DD`) this device has opened the Changelog page for.
  // A later entry in `lib/changelog` lights the drawer/account-menu notification dot until the page
  // is opened again. Device-local (MMKV) like `accountEmail`: it works before any account exists.
  lastSeenChangelogDate: z.iso.date().nullable().default(null),
  // Allow automatic new-character lessons between reviews and after the due queue is exhausted.
  introduceNewCardsEnabled: z.boolean().default(true),
  isAnswerBarHidden: z.boolean().default(false),
  isAutoplayEnabled: z.boolean().default(true),
  isAutoSearchEnabled: z.boolean().default(true),
  isPronunciationKeyboardVisible: z.boolean().default(false),
  // Whether the in-app diagnostics recorder is running (see `lib/diagnostics`): a rolling buffer
  // of logs, CPU/memory/thread samples, JS and UI-thread stalls, memory warnings, navigation and
  // errors that "Send diagnostic report" uploads. Off by default in EVERY build (development,
  // staging, production) — the user opts in from Settings → Diagnostics when asked to reproduce
  // a lag / memory / crash issue. Device-local (MMKV): it must be readable synchronously at
  // startup, before any account or database exists, so recording can begin immediately.
  isDiagnosticsRecordingEnabled: z.boolean().default(false),
  // Audio is on by default; the mute preference belongs to this device, not the actor.
  isMuted: z.boolean().default(false),
  // Quest calls pulse only when Han's avatar is pressed; controlled from the call's more menu.
  isQuestHapticsEnabled: z.boolean().default(true),
  // Shows the bottom performance overlay (build · CPU · memory · JS heap) in non-development
  // builds. Development builds always show it. Off by default; toggled from Settings → Diagnostics.
  isPerformanceOverlayEnabled: z.boolean().default(false),
  // Experimental outer-space backdrop in place of the off-white app chrome (see
  // `components.ui.space-backdrop`). Off by default; toggled from Settings → Admin → Admin settings
  // so it can be tried on a device without changing the shipped look.
  isSpaceBackgroundEnabled: z.boolean().default(false),
  // The audio input the grapheme-file-editor should record from (e.g. a specific
  // AirPods pair), stored as `{ name, type, uid }` (`null` = follow the system
  // default). We keep `name` + `type` alongside the `uid` because a Bluetooth
  // port's `uid` is NOT stable across reconnects/profile switches (A2DP↔HFP) —
  // `name`+`type` lets us re-resolve the device to its current uid when it
  // reconnects (see `usePreferredAudioInput`).
  //
  // This is an object schema, so `getDeviceSetting` returns a fresh reference on
  // every read. That would make `useSyncExternalStore`'s snapshot compare unequal
  // forever (→ infinite render loop) if a consumer subscribed to the object
  // directly — so the store memoizes the parsed snapshot per key (see
  // `getDeviceSetting`) and the hook destructures the primitive fields rather
  // than depending on the object reference (see `usePreferredAudioInput`).
  preferredAudioInput: z
    .object({
      name: z.string(),
      type: z.enum(["bluetooth", "headphones", "builtin", "other"]),
      uid: z.string(),
    })
    .nullable()
    .default(null),
  // Which characters count as "compounds" of a primitive on the Browse → Primitives tab.
  // `hsk-only` (default) restricts compounds to the graded syllabus — HSK-levelled characters
  // for simplified users, TBCL-levelled characters for traditional users. `all-tiers` widens the
  // set to every General Standard tier-1/2/3 character (simplified) or every "common" usage-tier
  // character (traditional). Device-local (MMKV) rather than account-synced: it's a transient view
  // preference scoped to this tab, toggled from the toolbar's tier-scope pill, and it feeds the
  // primitives query key so both sub-views (List / Compounds) re-derive off the same value.
  primitivesTierScope: z.enum(["hsk-only", "all-tiers"]).default("hsk-only"),
  // Which of the Browse → Levels pack sheet's surfaces the learner has collapsed to their header
  // with its chevron: Ready's Tones, Initials and Finals charts, and every later pack's progress.
  // The description opens by default; reference/progress surfaces start collapsed. Device-local.
  collapsedLevelsPackSurfaces: z
    .array(z.enum(["description", "tones", "initials", "finals", "progress"]))
    .default(["tones", "initials", "finals", "progress"]),
  // Whether Search returns exact matches only, toggled by the "Exact" option beside the search
  // field. Off by default: partial matches ("mal" → "malaria", "zhu" → zhuǎn) are listed below a
  // divider after the exact ones. Device-local (MMKV): a search preference, not account data.
  searchExactMatchOnly: z.boolean().default(false),
  // Which morpheme category the Search results are scoped to, chosen from the material tabs in the
  // results surface's header. `all` (default) mixes characters, words and sentence lookups;
  // `characters` restricts to single graphemes, `words` to multi-character vocabulary, and
  // `sentences` to the positional sentence breakdown. Device-local (MMKV) rather than
  // account-synced: it's a transient view preference, and it is deliberately NOT part of the
  // search query key — the query returns every category and the tab filters the rendered set, so
  // switching tabs never re-runs the search.
  searchResultsCategory: z.enum(["all", "characters", "words", "sentences"]).default("all"),
  // Whether Search shows its results as detailed tiles (default) or collapses them to small glyph
  // buttons, toggled by the −/+ button in the results surface's header (the same control Browse →
  // Levels puts on each level surface). `searchResultsViewMode` picks the detailed layout.
  searchResultsDetailsExpanded: z.boolean().default(true),
  // How Search lays out its detailed results, toggled from the results surface's header. `grid`
  // (default) is the square-tile grid; `list` is one full-width row per character (glyph, pinyin,
  // gloss, level/rank). Device-local (MMKV) rather than account-synced: it's a per-device view
  // preference that depends on screen size, and the same account on a phone and a tablet wants
  // different answers.
  searchResultsViewMode: z.enum(["grid", "list"]).default("grid"),
  // Whether example sentences (info panel "Words in context" / "Examples") show pinyin and
  // their English translation without a tap. Both default off so a learner reads the sentence
  // first; each sentence still has its own reveal buttons, and the section header toggles these
  // defaults. Device-local (MMKV): a reading preference, not account data.
  sentencePinyinShownByDefault: z.boolean().default(false),
  sentenceTranslationShownByDefault: z.boolean().default(false),
  // Which voice reads example sentences aloud: `system` (the platform's text-to-speech) or the id
  // of a downloaded neural voice (`lib/sentence/speech/voice-catalog`). `null` until the learner
  // first plays a sentence, which asks them to choose. Device-local (MMKV): the voice files are
  // downloaded to this device, so the choice cannot follow the account to another one.
  sentenceVoice: z.string().nullable().default(null),
  // Sentence read-aloud pace. `slow` speaks at 0.8× for following along; set on the voice page.
  sentenceSpeechRate: z.enum(["normal", "slow"]).default("normal"),
  showAdminActionsEnabled: z.boolean().default(false),
  // Whether a character's Compounds list also shows the compounds outside the browsing corpus
  // (Settings → Character Script). Off by default; the corpus already counts every HSK/TBCL
  // character and its primitives as common. Device-local (MMKV) and toggled from the Compounds
  // surface's header.
  showRareCompoundCharactersEnabled: z.boolean().default(false),
  // Whether Search includes rare characters — those with no frequency rank or tier 3 (see
  // `isObscureMorpheme`). Off by default so the common answer isn't buried; replaces the old
  // "Show All" footer button, which reset itself on every keystroke. Device-local (MMKV) and
  // toggled from the filter menu in the results surface's header.
  showRareSearchCharactersEnabled: z.boolean().default(false),
  // Whether the Search results' `characters` tab also lists primitives — decomposition-only
  // components, i.e. graphemes with no PRC tier (the same definition the Browse → Primitives tab
  // uses). Off by default: a learner searching for a character wants the character, not the
  // radicals it is built from. Device-local (MMKV) and toggled from the results surface's filter
  // menu, alongside "Show Rare Characters".
  showSearchPrimitivesEnabled: z.boolean().default(false),
  // Text size inside slide bodies only (see `lib/slide/text-size`), picked from the Learn toolbar's
  // More menu or the welcome slide. Device-local (MMKV): a phone and a tablet want different sizes,
  // and it must be readable before any account exists, since the welcome slide offers it first.
  slideTextSize: z.enum(SLIDE_TEXT_SIZES).default(DEFAULT_SLIDE_TEXT_SIZE),
  // An atomic registry of independently downloaded pack files. Coverage is read from each pack at install.
  installedLevelPacks: z
    .record(
      z.string(),
      z.object({
        bytes: z.number().int().positive(),
        file: z.string().regex(/^[a-f0-9]{64}\/(simplified|traditional)-[a-z0-9]+\.db$/),
        graphemes: z.array(z.string()),
        revision: z.string().regex(/^[a-f0-9]{64}$/),
        words: z.array(z.string()),
      }),
    )
    .default({}),
  // Downloaded on-device translation models (`lib/translation`), keyed by catalog id, with the
  // size and SHA-256 verified at install. A model counts as installed only while its file exists
  // with that size and the catalog still lists the same hash. `installedAt` makes every install a
  // new value, so screens re-render even when the OS purged the file and it was downloaded again.
  installedTranslationModels: z
    .record(
      z.string(),
      z.object({
        bytes: z.number().int().positive(),
        installedAt: z.number().int().positive(),
        sha256: z.string().regex(/^[a-f0-9]{64}$/),
      }),
    )
    .default({}),
  // The translation model the learner picked in Settings → Translation; `null` uses the
  // recommended downloaded model. Device-local: models are per-device downloads.
  activeTranslationModelId: z.string().nullable().default(null),
  // Which review kind the Learn deck serves, picked from the Learn toolbar's Reviews menu; anything
  // but `all` serves only due reviews of that kind and no new lessons (see `REVIEW_FOCUSES`).
  // Device-local like `studyEnvironment`: it is a "what am I practicing right now" choice.
  reviewFocus: z.enum(REVIEW_FOCUSES).default("all"),
  // Admin: the hardcoded head of the review queue, edited from the Admin panel's Review queue
  // section. The Learn deck serves these `user_card`s first, in this order, due or not, ahead of the
  // computed queue. An entry is spent once a live review of its card lands after `pinnedAt`, so
  // Undo (which soft-deletes that review) puts it back. Device-local: `userCardId`s belong to the
  // actor database this device has open, and an id it does not hold is passed over.
  pinnedReviewQueue: z
    .array(z.object({ pinnedAt: z.iso.datetime(), userCardId: z.string() }))
    .default([]),
  // Admin: which window edge the Admin panel grows from, picked from its More menu. `bottom` rests
  // above the primary navigation strip and any floating page tabs, keeping the toolbar in view.
  adminPanelPlacement: z.enum(["top", "bottom"]).default("top"),
  // The review-recharge "New Cards" toggle in the Learn deck. ON (offered once review recharge is
  // full) spends it on new curriculum cards: the next swipe opens a new card instead of a review,
  // unit after unit, until a unit ends with no review recharge left, which switches it back OFF.
  reviewRechargeNewCardsEnabled: z.boolean().default(false),
  // Where the learner is studying, picked from the Learn header's Environment menu; it narrows the
  // review queue and selects Touch or Voice mode (see `STUDY_ENVIRONMENTS`). Device-local (MMKV)
  // rather than account-synced: a phone on the commute and a desk tablet are different settings.
  studyEnvironment: z.enum(STUDY_ENVIRONMENTS).default("standard"),
  theme: z.enum(["light", "dark", "system"]).default("system"),
} as const satisfies Record<string, z.ZodType>;

export type DeviceSettingKey = keyof typeof deviceSettingsSchemas;
export type DeviceSettings = {
  [K in DeviceSettingKey]: z.infer<(typeof deviceSettingsSchemas)[K]>;
};
